import { colors } from '@globals/themes/defaultTheme'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { T } from '@transifex/react'
import { ReactNode } from 'react'
import { FieldValues, FormState } from 'react-hook-form'

type SubmitProps<T extends FieldValues> = {
  elementId?: string
  disabled?: boolean
  submitText?: ReactNode
  formState: FormState<T>
  fullWidth?: boolean
  variant?: 'contained' | 'outlined' | 'text'
  color?: 'primary' | 'secondary'
  form?: string
}

const Submit = <T extends FieldValues>({
  elementId,
  formState,
  submitText,
  fullWidth = false,
  // Use this prop if you need to disable the form for reasons beyond what can
  // be tracked in formState, e.g. a permission check, missing pre-requisite
  disabled = false,
  variant = 'contained',
  color,
  form,
}: SubmitProps<T>) => {
  const { isSubmitting, isLoading: formStateLoading } = formState

  // Disable button if form is submitting, loading, or explicitly disabled
  const isDisabled = isSubmitting || formStateLoading || disabled
  const isSubmittingOrPending = isSubmitting

  const renderSubmitText = () => {
    return submitText ? submitText : <T _str="Save" />
  }

  return (
    <Button
      id={elementId ?? 'btnSubmit'}
      variant={variant}
      type="submit"
      disabled={isDisabled}
      fullWidth={fullWidth}
      color={color}
      form={form}
      loading={isSubmittingOrPending}
      loadingIndicator={<CircularProgress size={16} sx={{ color: colors.white }} />}
      sx={{
        ...(isSubmittingOrPending && {
          '& .MuiButton-startIcon': {
            position: 'absolute',
          },
        }),
      }}>
      <span style={{ opacity: isSubmittingOrPending ? 0 : 1 }}>{renderSubmitText()}</span>
    </Button>
  )
}

export default Submit
